import React, { useMemo, useState, useCallback, useRef } from 'react';
import { DataTable } from '../components/organisms';
import { Button } from '../components/atoms';
import { useLanguages, useLanguageActions } from '../hooks/useLanguages';
import type { Column, TableAction } from '../types/table';
import type { Language, CreateLanguageRequest, UpdateLanguageRequest } from '../types/api/language';
import { formatDateForDisplay } from '../types/api/language';
import { LanguageFormModal } from '../components/organisms/LanguageFormModal';
import { DeleteConfirmationModal } from '../components/organisms/DeleteConfirmationModal';
import toast from 'react-hot-toast';

/**
 * LanguageManagementPage Component
 * 
 * Admin page for managing languages with comprehensive features:
 * - Server-side pagination
 * - Search functionality
 * - Language details display
 * - Status badges
 * - Language actions (create, edit, delete)
 * - Responsive design
 * 
 * Uses the DataTable component with custom hooks for data management
 */

export const LanguageManagementPage: React.FC = () => {
  const { fetchLanguages } = useLanguages();
  const { createLanguage, updateLanguage, deleteLanguage } = useLanguageActions();
  const refreshKeyRef = useRef(0);

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);

  // Handle create
  const handleCreate = useCallback(() => {
    setSelectedLanguage(null);
    setIsCreateModalOpen(true);
  }, []);

  // Handle edit
  const handleEdit = useCallback((language: Language) => {
    setSelectedLanguage(language);
    setIsEditModalOpen(true);
  }, []);

  // Handle delete
  const handleDelete = useCallback((language: Language) => {
    setSelectedLanguage(language);
    setIsDeleteModalOpen(true);
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refreshKeyRef.current += 1;
  }, []);

  // Handle create submit
  const handleCreateSubmit = useCallback(async (payload: CreateLanguageRequest, iconFile: File | null) => {
    try {
      await createLanguage(payload, iconFile);
      toast.success('Language created successfully');
      setIsCreateModalOpen(false);
      handleRefresh();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create language');
    }
  }, [createLanguage, handleRefresh]);

  // Handle edit submit
  const handleEditSubmit = useCallback(async (payload: UpdateLanguageRequest, iconFile: File | null) => {
    if (!selectedLanguage) return;
    
    try {
      await updateLanguage(selectedLanguage.languageId, payload, iconFile);
      toast.success('Language updated successfully');
      setIsEditModalOpen(false);
      setSelectedLanguage(null);
      handleRefresh();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update language');
    }
  }, [selectedLanguage, updateLanguage, handleRefresh]);

  // Handle delete confirm
  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedLanguage) return;
    
    try {
      await deleteLanguage(selectedLanguage.languageId);
      toast.success('Language deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedLanguage(null);
      handleRefresh();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete language');
    }
  }, [selectedLanguage, deleteLanguage, handleRefresh]);

  const columns = useMemo<Column<Language>[]>(
    () => [
      {
        id: 'name',
        header: 'Language',
        accessor: 'name',
        cell: (row) => (
          <div className="flex items-center gap-3">
            {/* Language Icon/Color */}
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold flex-shrink-0"
            //  style={{ backgroundColor: row.colorCode }}
            >
              {row.iconUrl ? (
                <img src={row.iconUrl} alt={row.name} className="w-full h-full object-cover rounded-lg" />
              ) : (
                <span>{row.isoCode.toUpperCase().slice(0, 2)}</span>
              )}
            </div>
            {/* Language Info */}
            <div>
              <p className="text-sm font-semibold text-gray-900">{row.name}</p>
              <p className="text-xs text-gray-500">{row.nameMm}</p>
            </div>
          </div>
        ),
        minWidth: '200px',
      },
      {
        id: 'isoCode',
        header: 'ISO Code',
        accessor: 'isoCode',
        cell: (row) => (
          <span className="text-sm text-gray-900 font-mono">{row.isoCode.toUpperCase()}</span>
        ),
        hideOnMobile: true,
      },
      {
        id: 'nativeName',
        header: 'Native Name',
        accessor: 'nativeName',
        cell: (row) => (
          <span className="text-sm text-gray-900">{row.nativeName}</span>
        ),
        hideOnMobile: true,
      },
      {
        id: 'isActive',
        header: 'Status',
        accessor: 'isActive',
        cell: (row) => {
          const isActive = row.isActive;
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                isActive
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {isActive ? 'Active' : 'Inactive'}
            </span>
          );
        },
      },
      {
        id: 'isRtl',
        header: 'RTL',
        accessor: 'isRtl',
        cell: (row) => (
          <span className="text-sm text-gray-600">
            {row.isRtl ? 'Yes' : 'No'}
          </span>
        ),
        hideOnMobile: true,
      },
      {
        id: 'contentCount',
        header: 'Content',
        accessor: 'contentCount',
        cell: (row) => (
          <span className="text-sm text-gray-900">{row.contentCount}</span>
        ),
        hideOnMobile: true,
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessor: 'createdAt',
        cell: (row) => (
          <span className="text-sm text-gray-600">
            {formatDateForDisplay(row.createdAt)}
          </span>
        ),
        hideOnMobile: true,
      },
    ],
    []
  );

  const actions = useMemo<TableAction<Language>[]>(
    () => [
      {
        id: 'edit',
        label: 'Edit',
        variant: 'ghost',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        ),
        onClick: handleEdit,
      },
      {
        id: 'delete',
        label: 'Delete',
        variant: 'danger',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        ),
        onClick: handleDelete,
      },
    ],
    [handleEdit, handleDelete]
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-900">Language Management</h1>
          <p className="text-gray-600 mt-1 text-left pl-3">Manage application languages</p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <Button
            onClick={handleCreate}
            leftIcon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
            }
          >
            Add Language
          </Button>
        </div>
      </div>

      {/* DataTable */}
      <DataTable<Language>
        columns={columns}
        fetchData={fetchLanguages}
        searchable
        searchPlaceholder="Search by name or ISO code..."
        initialPageSize={20}
        pageSizeOptions={[10, 20, 50, 100]}
        actions={actions}
        hoverable
        showRefreshButton
        onRefresh={handleRefresh}
        caption="Language management table"
        key={refreshKeyRef.current}
      />

      {/* Create Modal */}
      <LanguageFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={async ({ payload, iconFile }) => {
          await handleCreateSubmit(payload as CreateLanguageRequest, iconFile);
        }}
        mode="create"
      />

      {/* Edit Modal */}
      <LanguageFormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedLanguage(null);
        }}
        onSubmit={async ({ payload, iconFile }) => {
          await handleEditSubmit(payload, iconFile);
        }}
        mode="edit"
        language={selectedLanguage || undefined}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedLanguage(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Language"
        message={
          selectedLanguage
            ? `Are you sure you want to delete "${selectedLanguage.name}"? This action cannot be undone.`
            : 'Are you sure you want to delete this language?'
        }
      />
    </div>
  );
};

export default LanguageManagementPage;

